import { MILEAGE_DEVIATION_OPTIONS } from '@/Containers/ShiftSection/ShiftContainer/Contracts/MileageDeviation'

import type { ReportShellChip } from '../Components/ReportShell'
import { MILEAGE_LIST_FILTERS, type MileageFilterState } from '../Contracts/Report'
import { defaultPeriod, formatPeriodLabel } from './reportPeriod'
import { REPORT_OPTION_SOURCES } from './reportSources'

/** Початковий стан: поточний місяць, без меж відхилення, у км */
export const initialMileageFilters = (): MileageFilterState => ({
    period: defaultPeriod(),
    lists: { driver: [], vehicle: [], carrier: [] },
    statuses: [],
    unit: 'km',
    deviationMin: null,
    deviationMax: null,
})

/** Query-параметри API (формат MileageReportFilter): дати `YYYY-MM-DD`, списки — через кому */
export const buildMileageParams = (state: MileageFilterState): Record<string, string> => {
    const params: Record<string, string> = {
        from: state.period[0].format('YYYY-MM-DD'),
        to: state.period[1].format('YYYY-MM-DD'),
    }
    MILEAGE_LIST_FILTERS.forEach((name) => {
        const ids = state.lists[name].map((option) => option.value)
        if (ids.length > 0) params[name] = ids.join(',')
    })
    if (state.statuses.length > 0) params['status'] = state.statuses.join(',')
    if (state.deviationMin !== null) params['deviationMin'] = String(state.deviationMin)
    if (state.deviationMax !== null) params['deviationMax'] = String(state.deviationMax)
    // Одиниця має сенс лише з межами — без них не засмічуємо query
    if (state.unit !== 'km' && (state.deviationMin !== null || state.deviationMax !== null)) params['unit'] = state.unit
    return params
}

const formatBound = (value: number, unit: MileageFilterState['unit']): string =>
    `${value.toLocaleString('uk-UA', { maximumFractionDigits: 2 })} ${unit === 'percent' ? '%' : 'км'}`

/** Опис меж відхилення: «більше ніж 20 км і менше ніж 100 км» */
export const describeDeviationBounds = (state: MileageFilterState): string =>
    [
        state.deviationMin !== null ? `більше ніж ${formatBound(state.deviationMin, state.unit)}` : null,
        state.deviationMax !== null ? `менше ніж ${formatBound(state.deviationMax, state.unit)}` : null,
    ]
        .filter(Boolean)
        .join(' і ')

/**
 * Чіпи активних умов: період — коли не поточний місяць («×» повертає місяць: період обов'язковий),
 * довідники — через кому, статуси, межі відхилення.
 */
export const mileageShellChips = (
    state: MileageFilterState,
    onChange: (state: MileageFilterState) => void
): ReportShellChip[] => {
    const chips: ReportShellChip[] = []
    const periodLabel = formatPeriodLabel(state.period)
    if (periodLabel !== formatPeriodLabel(defaultPeriod())) {
        chips.push({
            key: 'period',
            label: 'Період',
            value: periodLabel,
            onRemove: () => onChange({ ...state, period: defaultPeriod() }),
        })
    }
    MILEAGE_LIST_FILTERS.forEach((name) => {
        const options = state.lists[name]
        if (options.length === 0) return
        chips.push({
            key: name,
            label: REPORT_OPTION_SOURCES[name].label,
            value: options.map((option) => option.label).join(', '),
            onRemove: () => onChange({ ...state, lists: { ...state.lists, [name]: [] } }),
        })
    })
    if (state.statuses.length > 0) {
        chips.push({
            key: 'status',
            label: 'Статус контролю',
            value: state.statuses
                .map((status) => MILEAGE_DEVIATION_OPTIONS.find((option) => option.value === status)?.label ?? status)
                .join(', '),
            onRemove: () => onChange({ ...state, statuses: [] }),
        })
    }
    if (state.deviationMin !== null || state.deviationMax !== null) {
        chips.push({
            key: 'deviation',
            label: 'Відхилення зміни',
            value: describeDeviationBounds(state),
            onRemove: () => onChange({ ...state, deviationMin: null, deviationMax: null }),
        })
    }
    return chips
}
